import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Select, Button, Table, Space, message, Modal, Tag, Card, Statistic, Row, Col, Tooltip, Upload, Image
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, EditOutlined,
  ExclamationCircleOutlined, ThunderboltOutlined, CheckCircleOutlined, InboxOutlined, EyeOutlined
} from '@ant-design/icons';
import './index.css';
import { workOrderApi } from '../../api/ticket';

const { Option } = Select;
const { TextArea } = Input;

const PRIORITY_MAP = { LOW: 'low', NORMAL: 'normal', HIGH: 'high' };
const PRIORITY_COLOR = { LOW: 'default', NORMAL: 'blue', HIGH: 'red' };
const STATUS_COLOR = { PENDING: 'orange', COMPLETED: 'green', ARCHIVED: 'default' };
const STATUS_LABEL = { PENDING: 'Pending', COMPLETED: 'Completed', ARCHIVED: 'Archived' };

function Ticket() {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();

  // Dashboard
  const [dashboard, setDashboard] = useState({ pending_count: 0, completed_count: 0, archived_count: 0, total_count: 0 });

  // Table
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});

  // Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null); // null=创建, object=编辑

  // 图片上传
  const [imageFileList, setImageFileList] = useState([]); // antd Upload fileList
  const [imageUrls, setImageUrls] = useState([]); // OSS URL 数组

  // 加载仪表盘
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await workOrderApi.dashboard();
      if (res.data?.code === 200) setDashboard(res.data.data);
    } catch (e) { console.error(e); }
  }, []);

  // 分页查询
  const fetchData = useCallback(async (page = 1, pageSize = 10, filters = currentFilters) => {
    setLoading(true);
    try {
      const res = await workOrderApi.page({ page, size: pageSize, ...filters });
      if (res.data?.code === 200) {
        const d = res.data.data;
        setTableData(d.records || []);
        setPagination({ current: d.current, pageSize: d.size, total: d.total });
      }
    } catch (e) {
      messageApi.error('Query failed');
    } finally {
      setLoading(false);
    }
  }, [currentFilters, messageApi]);

  useEffect(() => {
    fetchDashboard();
    fetchData();
  }, []);

  // 搜索
  const handleSearch = () => {
    const values = form.getFieldsValue();
    const filters = {};
    if (values.status) filters.status = values.status;
    if (values.keyword) filters.keyword = values.keyword.trim();
    setCurrentFilters(filters);
    fetchData(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentFilters({});
    fetchData(1, pagination.pageSize, {});
  };

  // 分页变化
  const handleTableChange = (pag) => {
    fetchData(pag.current, pag.pageSize);
  };

  // 图片上传处理
  const handleImageUpload = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    try {
      onProgress({ percent: 50 });
      const res = await workOrderApi.uploadImage(file);
      if (res.data?.code === 200) {
        const url = res.data.data.url;
        onProgress({ percent: 100 });
        onSuccess(res.data.data, file);
        // 追加到 imageUrls
        setImageUrls(prev => [...prev, url]);
      } else {
        onError(new Error(res.data?.message || 'Upload failed'));
      }
    } catch (e) {
      onError(e);
    }
  };

  const handleImageRemove = (file) => {
    // 从 imageUrls 中移除对应 URL
    const url = file.response?.url || file.url;
    if (url) {
      setImageUrls(prev => prev.filter(u => u !== url));
    }
    return true;
  };

  // 创建/编辑 Modal
  const openCreateModal = () => {
    setEditRecord(null);
    modalForm.resetFields();
    setImageFileList([]);
    setImageUrls([]);
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    modalForm.setFieldsValue({ title: record.title, content: record.content, priority: record.priority });
    // 回显已有图片
    const urls = record.image_urls || [];
    setImageUrls(urls);
    setImageFileList(urls.map((url, idx) => ({
      uid: `existing-${idx}`,
      name: `image-${idx}`,
      status: 'done',
      url,
      response: { url },
    })));
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await modalForm.validateFields();
      setModalLoading(true);
      const payload = { ...values, image_urls: imageUrls };
      if (editRecord) {
        // 更新
        const res = await workOrderApi.update({ id: editRecord.id, ...payload });
        if (res.data?.code === 200) {
          messageApi.success('Updated');
          setModalVisible(false);
          fetchData(pagination.current, pagination.pageSize);
          fetchDashboard();
        } else {
          messageApi.error(res.data?.message || 'Update failed');
        }
      } else {
        // 创建
        const res = await workOrderApi.create(payload);
        if (res.data?.code === 200) {
          messageApi.success('Created');
          setModalVisible(false);
          fetchData(1, pagination.pageSize);
          fetchDashboard();
        } else {
          messageApi.error(res.data?.message || 'Create failed');
        }
      }
    } catch (e) {
      if (e.errorFields) return; // form validate error
      messageApi.error('Operation failed');
    } finally {
      setModalLoading(false);
    }
  };

  // 状态更新（完成 / 归档）
  const handleStatusChange = async (record, status) => {
    try {
      const res = await workOrderApi.update({ id: record.id, status });
      if (res.data?.code === 200) {
        messageApi.success(`Status → ${STATUS_LABEL[status]}`);
        fetchData(pagination.current, pagination.pageSize);
        fetchDashboard();
      } else {
        messageApi.error(res.data?.message || 'Update failed');
      }
    } catch (e) { messageApi.error('Update failed'); }
  };

  // 删除
  const handleDelete = (record) => {
    confirmModal.confirm({
      title: 'Delete Confirmation',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure to delete ticket "${record.title}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await workOrderApi.delete(record.id);
          if (res.data?.code === 200) {
            messageApi.success('Deleted');
            fetchData(pagination.current, pagination.pageSize);
            fetchDashboard();
          } else {
            messageApi.error(res.data?.message || 'Delete failed');
          }
        } catch (e) { messageApi.error('Delete failed'); }
      },
    });
  };

  // 加急
  const handleUrgent = async (record) => {
    try {
      const res = await workOrderApi.urgent(record.id);
      if (res.data?.code === 200) {
        messageApi.success(res.data.message || 'Urgent notification sent');
        fetchData(pagination.current, pagination.pageSize);
      } else {
        messageApi.error(res.data?.message || 'Urgent failed');
      }
    } catch (e) { messageApi.error('Urgent failed'); }
  };

  // 列定义
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, align: 'center', fixed: 'left' },
    { title: 'Title', dataIndex: 'title', key: 'title', width: 200, ellipsis: true, render: (t) => t || '-' },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (s) => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s] || s}</Tag>
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: 100,
      render: (p) => <Tag color={PRIORITY_COLOR[p]}>{p}</Tag>
    },
    {
      title: 'Urgent', dataIndex: 'urgent_flag', key: 'urgent_flag', width: 80, align: 'center',
      render: (v, r) => v === 1
        ? <Tag color="red" icon={<ThunderboltOutlined />}>Urgent</Tag>
        : '-'
    },
    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', width: 120, render: (t) => t || '-' },
    { title: 'Created At', dataIndex: 'created_at', key: 'created_at', width: 160, render: (t) => t || '-' },
    { title: 'Updated At', dataIndex: 'updated_at', key: 'updated_at', width: 160, render: (t) => t || '-' },
    {
      title: 'Operation',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {/* 编辑：所有状态均可 */}
          {record.status !== 'ARCHIVED' && <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEditModal(record)} />
          </Tooltip>
    }

          {/* 加急：仅 PENDING 且未加急 */}
          {record.status === 'PENDING' && record.urgent_flag !== 1 && (
            <Tooltip title="Mark Urgent">
              <Button
                type="text"
                icon={<ThunderboltOutlined />}
                size="small"
                style={{ color: '#faad14' }}
                onClick={() => handleUrgent(record)}
              />
            </Tooltip>
          )}

          {record.status === 'PENDING' && (
            <Tooltip title="ARCHIVED">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                size="small"
                style={{ color: '#52c41a' }}
                onClick={() => handleStatusChange(record, 'ARCHIVED')}
              />
            </Tooltip>
          )}


          {/* 删除 */}
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="ticket-container">
      {contextHolder}
      {confirmModalContextHolder}

      {/* Dashboard */}
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={6}>
          <Card size="small"><Statistic title="Pending" value={dashboard.pending_count} valueStyle={{ color: '#fa8c16' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Completed" value={dashboard.completed_count} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Archived" value={dashboard.archived_count} valueStyle={{ color: '#8c8c8c' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Total" value={dashboard.total_count} valueStyle={{ color: '#1f63fb' }} /></Card>
        </Col>
      </Row>

      {/* 搜索区域 */}
      <div className="ticket-search-form">
        <Form form={form} layout="vertical">
          <div className="form-items">
            <Form.Item label="Status" name="status" className="form-item">
              <Select placeholder="Status" allowClear>
                <Option value="PENDING">Pending</Option>
                <Option value="COMPLETED">Completed</Option>
                <Option value="ARCHIVED">Archived</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Keyword" name="keyword" className="form-item">
              <Input placeholder="Search title / content" allowClear />
            </Form.Item>
          </div>
          <div className="form-buttons">
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
            </Space>
          </div>
        </Form>
      </div>

      {/* 表格 */}
      <div className="ticket-table">
        <div className="ticket-table-bar">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Create Ticket</Button>
        </div>
        <Table
          className="ticket-table-table"
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} records`,
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* 创建/编辑 Modal */}
      <Modal
        title={editRecord ? 'Edit Ticket' : 'Create Ticket'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        confirmLoading={modalLoading}
        destroyOnClose
        width={520}
      >
        <Form form={modalForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please input title' }]}>
            <Input placeholder="Ticket title" maxLength={100} />
          </Form.Item>
          <Form.Item name="content" label="Content" rules={[{ required: true, message: 'Please input content' }]}>
            <TextArea rows={4} placeholder="Describe the issue" maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="NORMAL">
            <Select>
              <Option value="LOW">LOW</Option>
              <Option value="NORMAL">NORMAL</Option>
              <Option value="HIGH">HIGH</Option>
            </Select>
          </Form.Item>

          {/* 图片上传 */}
          <Form.Item label="Images">
            <Upload
              listType="picture-card"
              fileList={imageFileList}
              customRequest={handleImageUpload}
              onRemove={handleImageRemove}
              onChange={({ fileList }) => setImageFileList(fileList)}
              accept="image/*"
              multiple
            >
              {imageFileList.length >= 9 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          {/* 编辑时允许修改状态 */}
          {editRecord && (
            <Form.Item name="status" label="Status" initialValue={editRecord.status}>
              <Select>
                <Option value="PENDING">Pending</Option>
                <Option value="COMPLETED">Completed</Option>
                <Option value="ARCHIVED">Archived</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default Ticket;
